import React, { useState, useEffect } from "react";
import { Button } from "../../../../Components/ui/button";
import { Input } from "../../../../Components/ui/input";
import { Label } from "../../../../Components/ui/label";
import { Card } from "../../../../Components/ui/card";
// import { Textarea } from "../../../../../Components/ui/textarea";
import { Textarea } from "../../../../Components/ui/textarea";
import { AlertTriangle, Info } from "lucide-react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "../../../../Components/ui/dialog";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '../../../../Components/ui/hover-card';
import { getSmsPartsCount } from "../../../../Pages/Campaign/CreateCampaign/utils/getSmsPartsCount";

// --- Universal SMS extractor ---
// Replace the extractSMSMessage function in CreateContentSMSCard.tsx with this enhanced version:

// Replace the extractSMSMessage function in CreateContentSMSCard.tsx with this enhanced version:

function extractSMSMessage(content: any): string {
  console.log("🔍 [SMS EXTRACTOR DEBUG] Starting extraction with content:", JSON.stringify(content, null, 2));
  
  if (!content) {
    console.log("❌ [SMS EXTRACTOR DEBUG] No content provided");
    return "";
  }

  // Test all possible paths and log results
  const paths = [
    { name: "Direct message", value: content.message, condition: typeof content.message === "string" && content.message.trim() },
    { name: "template.message", value: content.template?.message, condition: typeof content.template?.message === "string" && content.template?.message.trim() },
    { name: "template_content.message", value: content.template_content?.message, condition: typeof content.template_content?.message === "string" && content.template_content?.message.trim() },
    { name: "template_content.template_content.message", value: content.template_content?.template_content?.message, condition: typeof content.template_content?.template_content?.message === "string" && content.template_content?.template_content?.message.trim() },
    { name: "_translatedTemplate.template.message", value: content._translatedTemplate?.template?.message, condition: !!content._translatedTemplate?.template?.message },
    { name: "_translatedTemplate.message", value: content._translatedTemplate?.message, condition: !!content._translatedTemplate?.message },
    { name: "translated_template.template.message", value: content.translated_template?.template?.message, condition: !!content.translated_template?.template?.message },
    { name: "_debug_original_translated.template.message", value: content._debug_original_translated?.template?.message, condition: !!content._debug_original_translated?.template?.message },
    { name: "_debug_original_channel.translated_template.template.message", value: content._debug_original_channel?.translated_template?.template?.message, condition: !!content._debug_original_channel?.translated_template?.template?.message },
    { name: "api_type + template.message", value: content.template?.message, condition: content.api_type && content.template?.message },
  ];

  // Log all path attempts
  paths.forEach((path, index) => {
    console.log(`🔍 [SMS EXTRACTOR DEBUG] Path ${index + 1} - ${path.name}:`, {
      exists: path.condition,
      value: path.condition ? path.value : 'null/undefined/empty',
      type: typeof path.value,
      rawValue: path.value // Show the actual value even if falsy
    });
  });

  // Check for nested channels array
  if (content.channels && Array.isArray(content.channels)) {
    console.log("🔍 [SMS EXTRACTOR DEBUG] Found channels array:", content.channels);
    for (const channel of content.channels) {
      console.log(`🔍 [SMS EXTRACTOR DEBUG] Checking channel:`, channel);
      if (channel.channel_id === 2 && channel.translated_template?.template?.message) {
        console.log("✅ [SMS EXTRACTOR DEBUG] Found SMS in channels array:", channel.translated_template.template.message);
        return channel.translated_template.template.message;
      }
    }
  }

  // Check if content is a channel object itself
  if (content.channel_id === 2 && content.translated_template?.template?.message) {
    console.log("✅ [SMS EXTRACTOR DEBUG] Found SMS in channel object:", content.translated_template.template.message);
    return content.translated_template.template.message;
  }

  // Try all paths in order
  for (const path of paths) {
    if (path.condition && path.value) {
      console.log(`✅ [SMS EXTRACTOR DEBUG] Success with ${path.name}:`, path.value);
      return path.value;
    }
  }

  // Additional debugging - show the full structure
  console.log("❌ [SMS EXTRACTOR DEBUG] No SMS message found in any path");
  console.log("🔍 [SMS EXTRACTOR DEBUG] Content keys:", Object.keys(content));
  console.log("🔍 [SMS EXTRACTOR DEBUG] Content type:", typeof content);
  console.log("🔍 [SMS EXTRACTOR DEBUG] Is array:", Array.isArray(content));
  
  // Show nested structure for common paths
  if (content.template) {
    console.log("🔍 [SMS EXTRACTOR DEBUG] content.template keys:", Object.keys(content.template));
    console.log("🔍 [SMS EXTRACTOR DEBUG] content.template.message:", content.template.message);
  }
  
  if (content.translated_template) {
    console.log("🔍 [SMS EXTRACTOR DEBUG] content.translated_template keys:", Object.keys(content.translated_template));
    if (content.translated_template.template) {
      console.log("🔍 [SMS EXTRACTOR DEBUG] content.translated_template.template keys:", Object.keys(content.translated_template.template));
      console.log("🔍 [SMS EXTRACTOR DEBUG] content.translated_template.template.message:", content.translated_template.template.message);
    }
  }
  
  return "";
}

const textAreaIcon = () => (
  <div className="relative w-8 h-8 flex justify-center items-center">
    <div
      className="absolute rounded w-[3px] h-8 transform rotate-45"
      style={{ borderLeft: "3px solid #64748B" }}
    />
    <div
      className="absolute w-[3px] h-4 rounded transform rotate-45 translate-x-2 translate-y-2"
      style={{ borderLeft: "3px solid #64748B" }}
    />
  </div>
);

interface Props {
  initialContent?: any;
  onClose: () => void;
  onSave: (content: any) => void;
  campaignName?: string;
}

const CreateContentSMS: React.FC<Props> = ({
  initialContent,
  onClose,
  onSave,
  campaignName
}) => {
  // Use universal extractor for any shape of SMS template
  const [body, setBody] = useState<string>(extractSMSMessage(initialContent));

  const [partsCount, setPartsCount] = useState(1);
  const [showWarning, setShowWarning] = useState(false);

  const [bodyError, setBodyError] = useState<string>("");
  const isSaveDisabled = !body.trim() || !!bodyError;

  useEffect(() => {
    setBody(extractSMSMessage(initialContent));
    const initial = extractSMSMessage(initialContent);
    setBodyError(initial.trim() ? "" : "Message body is required.");
  }, [initialContent]);

  useEffect(() => {
    const smsInfo = getSmsPartsCount(body);
    setPartsCount(smsInfo.parts);
    setShowWarning(smsInfo.parts > 1);
    setBodyError(body.trim() ? "" : "Message body is required.");
  }, [body]);

  const formatText = (text: string) => {
    return text
      .replace(/\*(.*?)\*/g, "<strong>$1</strong>")
      .replace(/_(.*?)_/g, "<em>$1</em>")
      .replace(/~(.*?)~/g, "<s>$1</s>")
      .replace(/`(.*?)`/g, "<code>$1</code>");
  };

  const noContentSelected = !body.trim();

  const handleSave = () => {
    const text = (body ?? "").toString().trim()
    if (!body.trim()) {
    setBodyError("Message body is required.");
    return;
  }

  const smsInfo = getSmsPartsCount(text);
  const apiTemplate =
    initialContent?.template ||
    initialContent?.translated_template?.template ||
    initialContent?.template_content?.template ||
    {};

  // Template name from API, fallback to prop or default
  const templateName =
    apiTemplate?.name ||
    initialContent?.name ||
    initialContent?.template_name ||
    initialContent?.translated_template?.template?.name ||
    initialContent?.translated_template?.name ||
    (campaignName ? campaignName + "_SMS" : "sms_template");

  // Campaign name, robust
  const _campaignName =
    initialContent?.campaign_name ||
    initialContent?.translated_template?.campaign_name ||
    campaignName ||
    "";


  // Channel Config ID
  const channelConfigId =
    initialContent?.channel_config_id ||
    apiTemplate?.channel_config_id ||
    initialContent?.translated_template?.channel_config_id ||
    "";

  // Compose payload
  const finalSmsPayload = {
    channel_id: 2,
    channel_type: "SMS",
    part_count: smsInfo.parts,
    campaign_name: _campaignName,
    template: {
      name: templateName,
      message: body,
    },
    channel_config_id: channelConfigId,
  };

  console.log("SMS Save Payload:", finalSmsPayload);

  onSave(finalSmsPayload);
  onClose();
};



  return (
    <div>
      {/* Main layout: left = form, right = preview */}
      <div className="flex flex-col md:flex-row gap-6 mb-6 mt-4">
        {/* Left: Template form */}
        <div className="space-y-6 w-full max-w-[650px] md:w-3/5">
          <Card className="border p-4 rounded-lg text-left h-auto">
            <h2 className="text-base font-semibold mb-2">Content</h2>
            <div className="space-y-4 mt-6">
              <div>
                <Label htmlFor="bodyText">Body</Label>
                {showWarning && (
                  <Card className="flex items-center justify-between border text-black-100 rounded-md px-4 py-4 mt-4 text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                      <span>This content will consume {partsCount} SMS messages.</span>
                    </div>
                    <HoverCard>
                      <HoverCardTrigger asChild>
                        <span className="cursor-pointer">
                          <Info className="w-4 h-4 cursor-pointer hover:opacity-80" style={{ color: "#3A85F7" }} />
                        </span>
                      </HoverCardTrigger>
                      <HoverCardContent className="w-80 p-4 text-sm bg-white shadow-lg border border-gray-200 rounded-xl z-50">
                        <p className="font-semibold text-gray-600 mb-2">Why is the message split?</p>
                        <p className="text-gray-500 mb-2">
                          Most messages use a standard encoding called <strong>GSM-7</strong>, which allows up to <strong>160 characters</strong> in one SMS.
                        </p>
                        <p className="text-gray-500 mb-2">
                          If your message includes special symbols, emojis, or characters from non-English languages, it switches to a different encoding called <strong>UCS-2</strong>, which allows only <strong>70 characters</strong> per SMS.
                        </p>
                        <p className="text-gray-500">
                          When your message goes beyond these limits, it is automatically split into multiple parts.
                        </p>
                      </HoverCardContent>
                    </HoverCard>
                  </Card>
                )}
                <div className="relative mt-4 ">
                  <div className="text-xs text-gray-500 mt-1 flex justify-end mb-2">
                    Character Count: {body.length}
                  </div>
                  <Textarea
                    name="bodyText"
                    className="w-full h-[350px] border rounded p-2 text-[#020617] resize-none overflow-y-auto overflow-x-hidden break-all"
                    value={body}
                    placeholder="Hello"
                    aria-invalid={!!bodyError}
                    onChange={(e) => setBody(e.target.value)}
                  />
                  {bodyError && (                           /* ✅ NEW */
  <p className="text-red-500 text-xs font-medium mt-1 ml-1 italic">
    {bodyError}
  </p>
)}
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Right: SMS Preview */}
        <div className="w-full max-w-[500px]  md:w-2/5 border rounded-lg p-4 min-h-[590px] h-[90vh] flex flex-col">
          <h2 className="mb-2 mt-0 font-bold text-left">Content preview</h2>
          <div className="rounded-2xl p-4 h-full bg-[#F0F4F8] overflow-y-auto">
            {noContentSelected ? (
              <div className="flex flex-col items-center justify-center h-full w-full text-center">
                {textAreaIcon()}
                <div className="mt-6">
                  <p className="text-xl font-semibold">Mobile screen</p>
                </div>
                <div className="w-[125px] mt-2" style={{ fontWeight: 500, fontSize: "14px" }} />
              </div>
            ) : (
              <div className="w-full rounded p-2 mt-4 min-h-fit">
                <div
                  className="whitespace-pre-wrap break-words text-sm text-[#020617]"
                  dangerouslySetInnerHTML={{ __html: formatText(body) }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Buttons at the right bottom */}
      <div className="flex justify-end space-x-3 mt-6">
        <Dialog>
          <DialogTrigger asChild>
            <Button
              variant="outline"
              className="w-[80px] border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
            >
              Discard
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogDescription>
                Are you sure you want to discard this Template?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline" className="w-24">
                  Cancel
                </Button>
              </DialogClose>
              <Button className="w-24" onClick={onClose}>
                OK
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Button
          className="w-[100px] bg-[#3A85F7] text-white hover:bg-blue-600"
          onClick={handleSave}
          disabled={isSaveDisabled}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default CreateContentSMS;
